"use client";

import { useState } from "react";
import Image from "next/image";
import clsx from "clsx";
import { resolveOptimizedImageUrl } from "@/modules/web/utils/image";

type TeacherAvatarProps = {
  src?: string | null;
  name: string;
  /**
   * "circle" draws its own fixed-size ringed circle (profile header).
   * "fill" stretches to the parent box and inherits its shape, so an existing
   * image well keeps its dimensions and corner radius (teacher cards).
   */
  shape?: "circle" | "fill";
  /** Rendered size in px for `shape="circle"`. Drives the initials' scale. */
  size?: number;
  /** Initials type size in px for `shape="fill"`, where there is no `size`. */
  fillInitialsSize?: number;
  className?: string;
};

/**
 * Teacher avatar with an initials fallback.
 *
 * The shared `AvatarImage` falls back to one generic person PNG, so a grid of
 * teachers without photos rendered as a wall of identical grey icons. Initials
 * on a colour picked from the name give each teacher something distinct and
 * stable instead - the same teacher always gets the same colour, across
 * renders and across pages.
 *
 * A real photo is always preferred; the initials only stand in when there is no
 * image or the one on file fails to load.
 */
export default function TeacherAvatar({
  src,
  name,
  shape = "circle",
  size = 112,
  fillInitialsSize = 64,
  className,
}: TeacherAvatarProps) {
  const resolved = resolveOptimizedImageUrl(src) || "";
  // Records *which* src failed rather than a bare boolean, so a new src is
  // retried automatically - no reset effect, and no stale "broken" state stuck
  // on an image that was swapped out.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  const showInitials = !resolved || failedSrc === resolved;
  const accent = ACCENTS[hashName(name) % ACCENTS.length];
  const isFill = shape === "fill";

  return (
    <div
      style={isFill ? undefined : { width: size, height: size }}
      className={clsx(
        isFill
          ? "absolute inset-0"
          : "relative shrink-0 overflow-hidden rounded-full bg-white ring-4 ring-white shadow-[0_8px_24px_-8px_rgba(0,42,104,0.45)]",
        className,
      )}
    >
      {showInitials ? (
        // Tinted wash with coloured initials rather than a saturated full-bleed
        // block. At card size the block version filled most of the card, so a
        // grid of teachers without photos read as a wall of loud rectangles
        // competing with each other instead of a list of people.
        <div
          aria-hidden="true"
          className="flex h-full w-full items-center justify-center font-bold uppercase leading-none tracking-[0.06em]"
          style={{
            // Flat tint, not a gradient: at card size a gradient across this
            // much area drew attention to the placeholder itself.
            backgroundColor: `${accent}1F`,
            color: accent,
            fontSize: isFill ? fillInitialsSize : Math.round(size * 0.36),
          }}
        >
          {getInitials(name)}
        </div>
      ) : (
        <Image
          src={resolved}
          alt={name}
          fill
          sizes={isFill ? "(max-width: 640px) 100vw, 400px" : `${size}px`}
          className="object-cover"
          onError={() => setFailedSrc(resolved)}
        />
      )}
    </div>
  );
}

/**
 * One accent per teacher, used both for the initials and - at 8%-18% alpha -
 * for the wash behind them. Every value is dark enough to clear contrast
 * requirements against that near-white wash, and they sit close enough
 * together in tone that a full grid still reads as one palette.
 */
const ACCENTS = [
  "#002A68",
  "#1B7FA8",
  "#0F7A5C",
  "#4A3AAE",
  "#A55A12",
  "#9C2F52",
];

/** First and last initial - "Ahmed Mohamed Youssef" reads better as AY than AM. */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return "?";
  }

  const first = [...parts[0]][0] ?? "";

  if (parts.length === 1) {
    return first;
  }

  const last = [...parts[parts.length - 1]][0] ?? "";

  return `${first}${last}`;
}

/**
 * Stable hash so a teacher keeps the same colour between renders and pages.
 * Uses code points, so Arabic names spread across the palette as well as Latin
 * ones rather than all landing on the same bucket.
 */
function hashName(name: string): number {
  let hash = 0;

  for (const char of name.trim()) {
    hash = (hash * 31 + (char.codePointAt(0) ?? 0)) % 1_000_000_007;
  }

  return Math.abs(hash);
}
